import React, { useState, useEffect } from "react";
import {
  getAdminBookings,
  getAllProviders,
  updateBookingProvider,
  updateBookingConfirmation,
} from "./adminService";

import { ProviderSelect } from "./SubComponent/ProviderSelect";

/*
  - Make sure Bootstrap 5 CSS is linked in your public/index.html
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet">
*/

const ManagerScreen = () => {
  const token = localStorage.getItem("@uphealth_login_token");

  const [allProviders, setAllProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [unresponsiveBookings, setUnresponsiveBookings] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    pageSize: 15,
    totalItems: 0,
    totalPages: 1,
  });

  // We no longer need the confirmationInputs state
  // const [confirmationInputs, setConfirmationInputs] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const fetchData = async (page = 1, search = "") => {
    setLoading(true);
    setError(null);
    try {
      if (allProviders.length === 0) {
        const providersRes = await getAllProviders(token);
        setAllProviders(providersRes.data);
      }
      const bookingsRes = await getAdminBookings(
        token,
        page,
        paginationInfo.pageSize,
        search // Pass search term
      );

      if (bookingsRes.data && bookingsRes.data.pagination) {
        setAllBookings(bookingsRes.data.data);
        setPaginationInfo(bookingsRes.data.pagination);
      } else if (Array.isArray(bookingsRes.data)) {
        console.warn(
          "API response is not paginated. Please update your server."
        );
        setAllBookings(bookingsRes.data);
        setPaginationInfo({
          currentPage: 1,
          pageSize: 15,
          totalItems: bookingsRes.data.length,
          totalPages: 1,
        });
      } else {
        throw new Error("Received an unexpected data format from the API.");
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchData(1, debouncedSearchTerm);
  }, [token, debouncedSearchTerm]);

  useEffect(() => {
    if (Array.isArray(allBookings)) {
      const filtered = allBookings.filter((booking) => booking.paid === false);
      setUnresponsiveBookings(filtered);
    } else {
      setUnresponsiveBookings([]);
    }
  }, [allBookings]);

  const handleProviderChange = async (booking_id, new_provider_id) => {
    if (!new_provider_id) return;
    try {
      await updateBookingProvider(token, booking_id, new_provider_id);
      fetchData(paginationInfo.currentPage);
    } catch (err) {
      alert("Failed to update provider.");
      console.error(err);
    }
  };

  // --- MODIFIED CONFIRMATION HANDLER ---
  const handleConfirmationSubmit = async (booking_id, acceptanceStatus) => {
    try {
      // Send true for "Confirm", false for "Unconfirm"
      await updateBookingConfirmation(token, booking_id, acceptanceStatus); //
      fetchData(paginationInfo.currentPage);
    } catch (err) {
      alert(
        `Failed to ${acceptanceStatus ? "confirm" : "unconfirm"} booking. ${
          err.response?.data || err.message
        }`
      );
      console.error(err);
    }
  };

  const getBookingStatus = (booking) => {
    if (booking.paid) {
      return { text: "Paid", class: "bg-success" };
    }
    // 'acceptance' can be true, false, or null
    if (booking.acceptance === true) {
      return { text: "Confirmed (Not Paid)", class: "bg-primary" };
    }
    if (booking.acceptance === false) {
      return { text: "Unconfirmed (Declined)", class: "bg-danger" };
    }
    // If acceptance is null, it's pending
    return { text: "Pending (Not Paid)", class: "bg-warning text-dark" };
  };

  const handleNextPage = () => {
    if (paginationInfo.currentPage < paginationInfo.totalPages) {
      fetchData(paginationInfo.currentPage + 1, debouncedSearchTerm);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.currentPage > 1) {
      fetchData(paginationInfo.currentPage - 1, debouncedSearchTerm);
    }
  };

  // --- Render Logic ---

  if (loading && allProviders.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading Dashboard...</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      {/* Loading Overlay */}
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.8)", zIndex: 1050 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Updating...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4 p-3 bg-light rounded-3">
        <h2>Booking Management</h2>
        <p className="text-muted mb-0">
          Showing {unresponsiveBookings.length} unresponsive bookings on this
          page. Total items: {paginationInfo.totalItems}
        </p>
        <div className="input-group mt-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search by Client, ID, Provider, City, Country, or Service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Booking List - Responsive Grid */}
      <div className="row g-4">
        {unresponsiveBookings.map((booking) => {
          const status = getBookingStatus(booking);

          const nearbyProviders = allProviders.filter(
            (provider) =>
              provider.city.toLowerCase() === booking.city.toLowerCase() &&
              provider.country.toLowerCase() === booking.country.toLowerCase()
          );

          return (
            <div key={booking.booking_id} className="col-lg-6">
              <div className="card h-100 shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="card-title mb-0">
                    <strong className="small text-muted">Booking ID</strong>
                    <p className="font-monospace small mb-0">
                      {booking.booking_id}
                    </p>
                  </div>
                  <h5 className="card-title mb-0">
                    {booking.first_name} {booking.last_name}
                  </h5>
                  <span className={`badge ${status.class}`}>{status.text}</span>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <strong>Service</strong>
                      <p>{booking.care_service_name}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Current Provider</strong>
                      <p>{booking.provider_name || "N/A"}</p>
                    </div>
                    <div className="col-12 mt-3">
                      <strong>Patient Location (for matching)</strong>
                      <p className="text-muted">
                        {booking.city}, {booking.country}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card-footer bg-white border-top-0 pt-0">
                  {/* ... (Card footer with actions remains the same) ... */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label
                        htmlFor={`provider-select-${booking.booking_id}`}
                        className="form-label small"
                      >
                        Change Provider (Nearby Only)
                      </label>
                      <ProviderSelect
                        providers={nearbyProviders}
                        selectedProviderId={booking.provider_id}
                        onChange={(new_provider_id) =>
                          handleProviderChange(
                            booking.booking_id,
                            new_provider_id
                          )
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Actions</label>
                      {/* Use Bootstrap Dropdown */}
                      <div className="dropdown w-100">
                        <button
                          className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                          type="button"
                          id={`bookingMenu${booking.booking_id}`}
                          data-bs-toggle="dropdown" // This makes the dropdown work
                          aria-expanded="false"
                          disabled={!booking.provider_id || loading}
                          title={
                            !booking.provider_id
                              ? "Assign a provider first"
                              : "More actions"
                          }
                        >
                          <span>More Actions</span>
                          {/* This is the 3-dot icon */}
                          <i className="bi bi-three-dots-vertical"></i>
                        </button>
                        <ul
                          className="dropdown-menu w-100"
                          aria-labelledby={`bookingMenu${booking.booking_id}`}
                        >
                          <li>
                            <button
                              className="dropdown-item text-success"
                              type="button"
                              onClick={() =>
                                handleConfirmationSubmit(
                                  booking.booking_id,
                                  true
                                )
                              }
                            >
                              <i className="bi bi-check-circle me-2"></i>Confirm
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item text-danger"
                              type="button"
                              onClick={() =>
                                handleConfirmationSubmit(
                                  booking.booking_id,
                                  false
                                )
                              }
                            >
                              <i className="bi bi-x-circle me-2"></i>Unconfirm
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      <nav className="mt-4 d-flex justify-content-between align-items-center">
        <button
          className="btn btn-outline-secondary"
          onClick={handlePreviousPage}
          disabled={paginationInfo.currentPage <= 1 || loading}
        >
          &larr; Previous
        </button>
        <span className="text-muted">
          Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
        </span>
        <button
          className="btn btn-outline-secondary"
          onClick={handleNextPage}
          disabled={
            paginationInfo.currentPage >= paginationInfo.totalPages || loading
          }
        >
          Next &rarr;
        </button>
      </nav>
    </div>
  );
};

export default ManagerScreen;
